/**
 * Ruta aislada del participante: `/participar/:studyCode` (sin navegación ni elementos del panel).
 * Orquesta la máquina de estados pura (`features/experiment/stateMachine.ts`) con los efectos:
 * cámara/adaptador, red (cola de ensayos), pantalla completa y vigilancia de interrupciones.
 *
 * Modo simulación (`?simulacion=1[&error=40]`): usa `SyntheticEyeTracker`, solo si la compilación lo
 * permite (DEV o VITE_ENABLE_SYNTHETIC_CAPTURE=true) Y el servidor lo habilita; todo queda marcado
 * como `synthetic`.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import {
  INTERRUPTION_LABELS,
  type InterruptionReason,
  type PublicSessionCreated,
  type PublicStudyDto,
  type SessionProtocolDto,
  type TrialCreate,
} from "@gazelab/shared";
import { estimatePursuit, PURSUIT_DURATION_MS, PURSUIT_LEAD_IN_MS, type ImageBounds } from "@gazelab/analysis";
import { ApiError, api, errorMessage } from "../../services/api.js";
import {
  CAPTURE_STATES,
  PRESENTATION_STATES,
  initialContext,
  reducer,
  type MachineContext,
} from "../../features/experiment/stateMachine.js";
import type { EyeTrackerAdapter } from "../../features/experiment/adapters/EyeTrackerAdapter.js";
import { SyntheticEyeTracker } from "../../features/experiment/adapters/SyntheticEyeTracker.js";
import { TrialSaveQueue } from "../../features/experiment/saveQueue.js";
import { startInterruptionMonitor } from "../../features/experiment/interruptionMonitor.js";
import { preloadImages, revokePreloaded, type PreloadedImage, type PreloadProgress } from "../../features/experiment/preload.js";
import { estimateZoom, isDesktopChromium, readEnvironment } from "../../features/experiment/preflight.js";
import { measureFromRect, measureImageBounds } from "../../features/experiment/geometry.js";
import { TrialRecorder } from "../../features/experiment/timeline/trialRecorder.js";
import { ConsentScreen, ProfileScreen, WelcomeScreen, type ProfileData } from "./screens/InfoScreens.js";
import {
  CalibrationScreen,
  FullscreenScreen,
  InterruptedScreen,
  PauseScreen,
  PreflightScreen,
  ReadyScreen,
  ValidationScreen,
  type ValidationDecisionResult,
  type ValidationOutcome,
} from "./screens/SetupScreens.js";
import { FixationCross, PracticeRunner, StimulusImage, StimulusVideo, type StimulusHandle, type VideoCapture } from "./screens/Presentation.js";
import { PursuitScreen } from "./screens/Pursuit.js";
import { AbortedScreen, CompleteScreen, type DeletionRequestInfo } from "./screens/EndScreens.js";
import { Panel, SimulationBanner } from "./ui.js";

/** El rastreador sintético solo existe en compilaciones de desarrollo o habilitadas explícitamente. */
export const SYNTHETIC_BUILD_ALLOWED = import.meta.env.DEV || import.meta.env.VITE_ENABLE_SYNTHETIC_CAPTURE === "true";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function ParticipantPage() {
  const { studyCode = "" } = useParams();
  const [search] = useSearchParams();
  const simRequested = search.get("simulacion") === "1";
  const simErrorPx = Math.min(1000, Math.max(0, Number(search.get("error") ?? 40) || 0));

  const [study, setStudy] = useState<PublicStudyDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ctx, dispatch] = useReducer(reducer, undefined, initialContext);
  const ctxRef = useRef<MachineContext>(ctx);
  ctxRef.current = ctx;

  const simulation = simRequested && SYNTHETIC_BUILD_ALLOWED && study?.syntheticCaptureAllowed === true;

  const profileRef = useRef<ProfileData | null>(null);
  const sessionRef = useRef<PublicSessionCreated | null>(null);
  const [protocol, setProtocol] = useState<SessionProtocolDto | null>(null);
  const imagesRef = useRef<Map<string, PreloadedImage>>(new Map());
  const [preload, setPreload] = useState<PreloadProgress & { ready: boolean; error: string | null }>({
    done: 0,
    total: 0,
    loadedBytes: 0,
    totalBytes: 0,
    ready: false,
    error: null,
  });
  /** Hay una captura de la validación con punto en movimiento en curso. */
  const pursuitActiveRef = useRef(false);
  const pursuitStartedAtRef = useRef("");
  const adapterRef = useRef<EyeTrackerAdapter | null>(null);
  const queueRef = useRef<TrialSaveQueue | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [hostVisible, setHostVisible] = useState(false);
  const stimulusHandleRef = useRef<StimulusHandle | null>(null);
  const activeTrialRef = useRef<TrialRecorder | null>(null);
  const zoomRef = useRef<number | null>(null);
  const testModeNoFullscreenRef = useRef(false);
  const fullscreenGrantedRef = useRef(false);
  const calibrationCountRef = useRef(0);
  const syntheticSeedRef = useRef(crypto.randomUUID());
  const [preparingSession, setPreparingSession] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [saveProblem, setSaveProblem] = useState(0);
  const [abortMessage, setAbortMessage] = useState("Participación cancelada. No hay ninguna penalización.");
  const finalizedRef = useRef(false);

  // --- Carga del estudio ---
  useEffect(() => {
    let alive = true;
    api
      .publicStudy(studyCode)
      .then((s) => alive && setStudy(s))
      .catch((e) => alive && setLoadError(e instanceof ApiError && e.status === 404 ? "Este estudio no existe o no está activo." : errorMessage(e)));
    return () => {
      alive = false;
    };
  }, [studyCode]);

  // --- Adaptador ---
  const getAdapter = useCallback(async (): Promise<EyeTrackerAdapter> => {
    if (adapterRef.current) return adapterRef.current;
    let adapter: EyeTrackerAdapter;
    if (simulation) {
      adapter = new SyntheticEyeTracker({ seed: syntheticSeedRef.current, validationErrorPx: simErrorPx });
    } else {
      // Import dinámico: jsPsych/WebGazer (y su CSS) solo se cargan en modo real.
      const mod = await import("../../features/experiment/adapters/WebGazerEyeTracker.js");
      adapter = new mod.WebGazerEyeTracker();
    }
    if (!hostRef.current) throw new Error("Contenedor del rastreador no disponible");
    adapter.mount(hostRef.current);
    adapterRef.current = adapter;
    return adapter;
  }, [simulation, simErrorPx]);

  const cleanup = useCallback(async () => {
    stimulusHandleRef.current?.cancel();
    activeTrialRef.current = null;
    const adapter = adapterRef.current;
    adapterRef.current = null;
    setHostVisible(false);
    await adapter?.dispose().catch(() => {});
    revokePreloaded(imagesRef.current);
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
  }, []);

  // Al salir de la ruta: apagar cámara y liberar imágenes.
  useEffect(() => {
    return () => {
      void cleanup();
    };
  }, [cleanup]);

  // --- Interrupciones ---
  const enqueue = (t: TrialCreate) => queueRef.current?.enqueue(t);

  const handleInterrupt = (reason: InterruptionReason) => {
    if (!CAPTURE_STATES.has(ctxRef.current.state)) return;
    // Fotogramas presentados hasta ahora (video) antes de detener la reproducción.
    const video = stimulusHandleRef.current?.videoSnapshot?.();
    stimulusHandleRef.current?.cancel();
    adapterRef.current?.cancelActivePhase();
    setHostVisible(false);
    const rec = activeTrialRef.current;
    activeTrialRef.current = null;
    if (rec && adapterRef.current) {
      const now = performance.now();
      const visibility = document.visibilityState;
      // Detener la captura de inmediato; el ensayo se guarda como INTERRUMPIDO, nunca como completado.
      adapterRef.current
        .stopTrial()
        .catch(() => [])
        .then((samples) => enqueue(rec.interrupt({ samples, nowMs: now, reason, visibility, video })));
    } else if (pursuitActiveRef.current && adapterRef.current) {
      // Seguimiento interrumpido: se descarta (se repetirá tras recalibrar).
      pursuitActiveRef.current = false;
      void adapterRef.current.stopTrial().catch(() => []);
    }
    dispatch({ type: "INTERRUPT", reason });
  };
  const interruptRef = useRef(handleInterrupt);
  interruptRef.current = handleInterrupt;

  useEffect(() => {
    if (!CAPTURE_STATES.has(ctx.state)) return;
    const stop = startInterruptionMonitor({
      onInterrupt: (r) => interruptRef.current(r),
      requireFullscreen: !testModeNoFullscreenRef.current,
    });
    const unLost = adapterRef.current?.onLost((r) => interruptRef.current(r));
    return () => {
      stop();
      unLost?.();
    };
  }, [ctx.state]);

  // Pausa solicitada con la tecla P (se aplica al terminar la imagen actual).
  useEffect(() => {
    if (!PRESENTATION_STATES.has(ctx.state)) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "p" || e.key === "P") dispatch({ type: "REQUEST_PAUSE" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ctx.state]);

  // TRIAL_SAVE: el ensayo ya está en la cola (envío en segundo plano); continuar sin bloquear.
  useEffect(() => {
    if (ctx.state === "TRIAL_SAVE") dispatch({ type: "TRIAL_SAVED" });
  }, [ctx.state]);

  // --- Final ---
  const finalize = useCallback(
    async (force: boolean) => {
      setFinalizing(true);
      const q = queueRef.current;
      if (q) {
        if (force) q.clear();
        else {
          q.retryFailed();
          const ok = await q.flush();
          if (!ok) {
            setSaveProblem(q.failed.length);
            setFinalizing(false);
            return;
          }
        }
      }
      const s = sessionRef.current;
      if (s) await api.completeSession(s.sessionId, s.accessToken).catch(() => {});
      await cleanup();
      setFinalizing(false);
    },
    [cleanup],
  );

  useEffect(() => {
    if (finalizedRef.current) return;
    if (ctx.state === "COMPLETE") {
      finalizedRef.current = true;
      void finalize(false);
    } else if (ctx.state === "ABORTED") {
      finalizedRef.current = true;
      void (async () => {
        const s = sessionRef.current;
        if (s) {
          await queueRef.current?.flush();
          const reason = ctx.abortReason ?? "other";
          await api
            .abortSession(s.sessionId, s.accessToken, { status: reason === "participant_cancel" ? "aborted" : "interrupted", reason })
            .catch(() => {});
        }
        await cleanup();
      })();
    }
  }, [ctx.state, ctx.abortReason, finalize, cleanup]);

  const abort = (reason: InterruptionReason, message?: string) => {
    if (message) setAbortMessage(message);
    else if (reason !== "participant_cancel") setAbortMessage(`La sesión se detuvo: ${INTERRUPTION_LABELS[reason]}.`);
    dispatch({ type: "ABORT", reason });
  };

  // --- Pasos ---
  const startCamera = async () => {
    zoomRef.current = estimateZoom(readEnvironment());
    if (ctx.resuming && adapterRef.current) {
      // Tras perder la cámara se reinicializa por completo.
      const old = adapterRef.current;
      adapterRef.current = null;
      await old.dispose().catch(() => {});
    }
    const adapter = await getAdapter();
    setHostVisible(!simulation);
    try {
      await adapter.initializeCamera();
    } catch (e) {
      adapterRef.current = null;
      await adapter.dispose().catch(() => {});
      throw e;
    } finally {
      setHostVisible(false);
    }
    dispatch({ type: "PREFLIGHT_OK" });
  };

  const createSessionAndProtocol = async () => {
    const profile = profileRef.current;
    if (!study || !profile) throw new Error("Faltan datos del perfil");
    const env = readEnvironment();
    const res = await api.createSession(studyCode, {
      consent: true,
      consentVersion: study.consentVersion,
      ageYears: profile.ageYears,
      visionCorrection: profile.visionCorrection,
      selfReportedVision: profile.selfReportedVision,
      source: simulation ? "synthetic" : "webgazer",
      deviceMeta: {
        userAgent: env.userAgent.slice(0, 400),
        browserSupported: isDesktopChromium(env),
        screenWidth: Math.round(window.screen.width),
        screenHeight: Math.round(window.screen.height),
        viewportWidth: Math.round(window.innerWidth),
        viewportHeight: Math.round(window.innerHeight),
        devicePixelRatio: window.devicePixelRatio,
        estimatedZoom: zoomRef.current,
        lighting: profile.lighting,
        distanceCm: profile.distanceCm,
        fullscreenGranted: fullscreenGrantedRef.current,
        isTestMode: simulation || testModeNoFullscreenRef.current,
      },
    });
    sessionRef.current = res;
    const proto = await api.protocol(res.sessionId, res.accessToken);
    setProtocol(proto);
    queueRef.current = new TrialSaveQueue({
      send: (t) => api.postTrial(res.sessionId, res.accessToken, t),
      onFatal: () => interruptRef.current("network_error"),
      onRejected: (t, e) => console.warn(`Ensayo ${t.sequenceIndex} rechazado por el servidor: ${errorMessage(e)}`),
    });
    setPreload({ done: 0, total: proto.images.length, loadedBytes: 0, totalBytes: 0, ready: false, error: null });
    void preloadImages(
      proto.images,
      (id, onBytes) => api.fetchStimulus(id, res.accessToken, onBytes),
      (p) => setPreload((prev) => ({ ...prev, ...p })),
    )
      .then((map) => {
        imagesRef.current = map;
        setPreload((p) => ({ ...p, ready: true }));
      })
      .catch((e) => setPreload((p) => ({ ...p, error: errorMessage(e) })));
    dispatch({
      type: "SESSION_READY",
      totalImages: proto.images.length,
      revalidateEvery: proto.settings.revalidateEveryNTrials,
      practiceEnabled: proto.settings.practiceEnabled,
      pursuitEnabled: proto.settings.pursuitValidation && !proto.pursuitDone,
    });
  };

  const afterFullscreen = async () => {
    if (ctxRef.current.sessionReady) {
      dispatch({ type: "FULLSCREEN_OK" });
      return;
    }
    setPreparingSession(true);
    try {
      await createSessionAndProtocol();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 422 || e.code === "demo_limit")) {
        await cleanup();
        abort("other", `${e.message} No se registró ningún dato.`);
        return;
      }
      throw e;
    } finally {
      setPreparingSession(false);
    }
  };

  const enterFullscreen = async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen({ navigationUI: "hide" });
    } catch {
      throw new Error("El navegador no permitió la pantalla completa.");
    }
    if (!document.fullscreenElement) throw new Error("El navegador no permitió la pantalla completa.");
    fullscreenGrantedRef.current = true;
    testModeNoFullscreenRef.current = false;
    // Esperar a que terminen los eventos de cambio de tamaño de la transición.
    await sleep(400);
    await afterFullscreen();
  };

  const skipFullscreenTestMode = async () => {
    if (!simulation) throw new Error("Solo disponible en modo simulación.");
    testModeNoFullscreenRef.current = true;
    await afterFullscreen();
  };

  const runCalibration = async () => {
    const adapter = await getAdapter();
    setHostVisible(true);
    try {
      await adapter.calibrate(calibrationCountRef.current > 0);
      calibrationCountRef.current++;
    } finally {
      setHostVisible(false);
    }
    dispatch({ type: "CALIBRATION_DONE" });
  };

  const runValidation = async (): Promise<ValidationOutcome> => {
    const adapter = await getAdapter();
    if (!protocol) throw new Error("Protocolo no disponible");
    setHostVisible(true);
    try {
      return await adapter.validate({ pointCount: protocol.settings.validationPoints, roiRadiusPx: protocol.settings.quality.roiRadiusPx });
    } finally {
      setHostVisible(false);
    }
  };

  const decideValidation = async (
    outcome: ValidationOutcome,
    decision: "continue" | "recalibrate" | "override" | "cancel",
    reason?: string,
  ): Promise<ValidationDecisionResult> => {
    const s = sessionRef.current;
    if (!s) return { ok: false, message: "Sesión no disponible" };
    // El servidor acepta ensayos solo si la corrida de calibración MÁS RECIENTE permite continuar:
    // se vacía la cola antes de registrar una nueva corrida para no invalidar ensayos ya capturados.
    await queueRef.current?.flush();
    let res;
    try {
      res = await api.postCalibration(s.sessionId, s.accessToken, {
        clientCalibrationId: crypto.randomUUID(),
        phase: ctxRef.current.validationPhase,
        startedAt: outcome.startedAt,
        endedAt: outcome.endedAt,
        metrics: outcome.metrics,
        decision,
        ...(reason ? { overrideReason: reason } : {}),
      });
    } catch (e) {
      if (decision === "override" && e instanceof ApiError && (e.status === 403 || e.status === 401)) {
        return {
          ok: false,
          message: "La excepción requiere que la persona investigadora haya iniciado sesión en el panel en este navegador y que el protocolo la permita.",
        };
      }
      throw e;
    }
    if (decision === "recalibrate") {
      dispatch({ type: "VALIDATION_RECALIBRATE" });
      return { ok: true };
    }
    if (decision === "cancel") {
      abort("participant_cancel");
      return { ok: true };
    }
    if (res.canProceed) {
      dispatch({ type: "VALIDATION_ACCEPTED" });
      return { ok: true };
    }
    return {
      ok: false,
      classification: res.classification,
      message: `El servidor clasificó la validación como «${res.classification}», por debajo del mínimo del estudio. Recalibra o solicita una excepción.`,
    };
  };

  // --- Estímulo ---
  const currentImage = protocol?.images[ctx.nextIndex];
  const preloaded = currentImage ? imagesRef.current.get(currentImage.imageId) : undefined;

  const onStimulusVisible = async (_el: HTMLImageElement | HTMLVideoElement, measured: ImageBounds): Promise<number> => {
    const adapter = adapterRef.current;
    if (!adapter || !currentImage || !protocol) throw new Error("Estado inválido para iniciar ensayo");
    // El rectángulo pintado se calcula con las dimensiones decodificadas; las dimensiones naturales
    // registradas son las del servidor (el API exige que coincidan con el estímulo guardado).
    const bounds: ImageBounds = { ...measured, naturalWidth: currentImage.naturalWidth, naturalHeight: currentImage.naturalHeight };
    const isVideo = currentImage.kind === "video";
    const startedAtIso = new Date().toISOString();
    const t0 = await adapter.startTrial(currentImage.imageId, {
      bounds,
      durationMs: isVideo ? (currentImage.durationMs ?? preloaded?.durationMs ?? 0) : protocol.settings.imageDurationMs,
      kind: isVideo ? "video" : "image",
    });
    activeTrialRef.current = new TrialRecorder({
      clientTrialId: crypto.randomUUID(),
      imageId: currentImage.imageId,
      sequenceIndex: currentImage.sequenceIndex,
      bounds,
      t0,
      startedAtIso,
    });
    return t0;
  };

  const onVideoEnded = async (video: HTMLVideoElement, capture: VideoCapture) => {
    const rec = activeTrialRef.current;
    const adapter = adapterRef.current;
    if (!rec || !adapter) return;
    activeTrialRef.current = null;
    const now = performance.now();
    const samples = await adapter.stopTrial();
    let endBounds: ImageBounds | null;
    try {
      const r = video.getBoundingClientRect();
      const m = measureFromRect(r, video.videoWidth, video.videoHeight, window);
      endBounds = { ...m, naturalWidth: rec.info.bounds.naturalWidth, naturalHeight: rec.info.bounds.naturalHeight };
    } catch {
      endBounds = null;
    }
    const payload = rec.finish({ samples, nowMs: now, endBounds, visibility: document.visibilityState, video: capture });
    enqueue(payload);
    if (payload.status === "interrupted") dispatch({ type: "INTERRUPT", reason: payload.interruptionReason ?? "viewport_resize" });
    else dispatch({ type: "STIMULUS_DONE" });
  };

  // --- Validación con punto en movimiento ---
  const startPursuit = async (): Promise<number> => {
    const adapter = adapterRef.current;
    if (!adapter) throw new Error("Rastreador no disponible");
    const w = window.innerWidth;
    const h = window.innerHeight;
    const bounds: ImageBounds = {
      viewportWidth: w,
      viewportHeight: h,
      displayedLeft: 0,
      displayedTop: 0,
      displayedWidth: w,
      displayedHeight: h,
      naturalWidth: w,
      naturalHeight: h,
      devicePixelRatio: window.devicePixelRatio,
    };
    pursuitActiveRef.current = true;
    pursuitStartedAtRef.current = new Date().toISOString();
    return adapter.startTrial("pursuit", { bounds, durationMs: PURSUIT_LEAD_IN_MS + PURSUIT_DURATION_MS, kind: "pursuit" });
  };

  const finishPursuit = async () => {
    const adapter = adapterRef.current;
    const s = sessionRef.current;
    if (!adapter || !s || !pursuitActiveRef.current) return;
    pursuitActiveRef.current = false;
    const raw = await adapter.stopTrial();
    const w = window.innerWidth;
    const h = window.innerHeight;
    // Tiempo relativo al inicio del movimiento (el punto estuvo quieto PURSUIT_LEAD_IN_MS).
    const moving = raw.map((p) => ({ tMs: p.tMs - PURSUIT_LEAD_IN_MS, x: p.xScreen, y: p.yScreen }));
    const est = estimatePursuit(moving, w, h);
    const totalMs = PURSUIT_LEAD_IN_MS + PURSUIT_DURATION_MS;
    await api.postPursuit(s.sessionId, s.accessToken, {
      startedAt: pursuitStartedAtRef.current,
      endedAt: new Date().toISOString(),
      metrics: {
        method: adapterRef.current?.method.slice(0, 300) ?? "desconocido",
        durationMs: totalMs,
        totalSamples: raw.length,
        samplesPerSecond: (raw.length * 1000) / totalMs,
        lagMs: est.lagMs,
        correlation: est.correlation === null ? null : Math.max(-1, Math.min(1, est.correlation)),
        meanErrorPx: est.meanErrorPx,
        meanErrorRelative: est.meanErrorPx === null ? null : est.meanErrorPx / Math.hypot(w, h),
        viewportWidth: w,
        viewportHeight: h,
      },
    });
    dispatch({ type: "PURSUIT_DONE" });
  };

  const onStimulusElapsed = async (img: HTMLImageElement) => {
    const rec = activeTrialRef.current;
    const adapter = adapterRef.current;
    if (!rec || !adapter) return;
    activeTrialRef.current = null;
    const now = performance.now();
    const samples = await adapter.stopTrial();
    let endBounds: ImageBounds | null;
    try {
      const m = measureImageBounds(img);
      endBounds = { ...m, naturalWidth: rec.info.bounds.naturalWidth, naturalHeight: rec.info.bounds.naturalHeight };
    } catch {
      endBounds = null;
    }
    const payload = rec.finish({ samples, nowMs: now, endBounds, visibility: document.visibilityState });
    enqueue(payload);
    if (payload.status === "interrupted") dispatch({ type: "INTERRUPT", reason: payload.interruptionReason ?? "viewport_resize" });
    else dispatch({ type: "STIMULUS_DONE" });
  };

  // --- Render ---
  const deletionInfo: DeletionRequestInfo | null =
    sessionRef.current && study ? { participantCode: sessionRef.current.participantCode, contactInfo: study.contactInfo } : null;

  let screen: React.ReactNode = null;
  if (loadError) {
    screen = (
      <Panel title="Estudio no disponible">
        <p data-testid="study-error">{loadError}</p>
      </Panel>
    );
  } else if (!study) {
    screen = (
      <Panel title="Cargando…">
        <p role="status">Cargando el estudio…</p>
      </Panel>
    );
  } else if (simRequested && !simulation) {
    screen = (
      <Panel title="Modo simulación no disponible">
        <p data-testid="simulation-unavailable">
          El modo simulación no está habilitado en esta instalación. Abre el enlace sin «?simulacion=1» para participar con la
          cámara.
        </p>
      </Panel>
    );
  } else {
    const settings = protocol?.settings;
    switch (ctx.state) {
      case "WELCOME":
        screen = <WelcomeScreen study={study} onStart={() => dispatch({ type: "START" })} />;
        break;
      case "CONSENT":
        screen = (
          <ConsentScreen
            study={study}
            retentionDays={study.retentionDays}
            onAccept={() => dispatch({ type: "CONSENT_ACCEPTED" })}
            onCancel={() => abort("participant_cancel", "No aceptaste participar. No se registró ningún dato.")}
          />
        );
        break;
      case "PROFILE":
        screen = (
          <ProfileScreen
            study={study}
            onSubmit={(p) => {
              profileRef.current = p;
              dispatch({ type: "PROFILE_DONE" });
            }}
            onCancel={() => abort("participant_cancel", "Cancelaste tu participación. No se registró ningún dato.")}
          />
        );
        break;
      case "PREFLIGHT":
        screen = <PreflightScreen simulation={simulation} resuming={ctx.resuming} onStartCamera={startCamera} onCancel={() => abort("participant_cancel")} />;
        break;
      case "FULLSCREEN":
        screen = (
          <FullscreenScreen
            kind={study.stimulusKind}
            simulation={simulation}
            preparing={preparingSession}
            onEnter={enterFullscreen}
            onSkipTestMode={skipFullscreenTestMode}
            onCancel={() => abort("participant_cancel")}
          />
        );
        break;
      case "PRACTICE":
        screen = settings && (
          <PracticeRunner fixationMs={settings.fixationDurationMs} durationMs={settings.imageDurationMs} onDone={() => dispatch({ type: "PRACTICE_DONE" })} />
        );
        break;
      case "CALIBRATION":
        // En estudios de video, la calibración empieza cuando todos los videos ya se descargaron:
        // así la descarga no hace perder vigencia a la calibración.
        screen = (
          <CalibrationScreen
            recalibrating={calibrationCountRef.current > 0}
            onRun={runCalibration}
            waitFor={protocol?.images.some((i) => i.kind === "video") ? preload : null}
          />
        );
        break;
      case "VALIDATION":
        screen = settings && (
          <ValidationScreen
            key={`${calibrationCountRef.current}-${ctx.completedTrials}`}
            phase={ctx.validationPhase}
            settings={settings}
            onRun={runValidation}
            onDecision={decideValidation}
          />
        );
        break;
      case "PURSUIT":
        screen = <PursuitScreen onStart={startPursuit} onFinish={finishPursuit} />;
        break;
      case "READY":
        screen = (
          <ReadyScreen
            preload={preload}
            kind={protocol?.stimulusKind ?? "image"}
            audioCheck={Boolean(settings?.videoAudio) && Boolean(protocol?.images.some((i) => i.kind === "video" && i.hasAudio))}
            onBegin={() => dispatch({ type: "BEGIN" })}
          />
        );
        break;
      case "FIXATION_CROSS":
        screen = settings && <FixationCross durationMs={settings.fixationDurationMs} onDone={() => dispatch({ type: "FIXATION_DONE" })} />;
        break;
      case "STIMULUS":
        screen =
          settings && preloaded?.kind === "video" ? (
            <StimulusVideo
              key={ctx.nextIndex}
              url={preloaded.url}
              muted={!settings.videoAudio}
              onVisible={onStimulusVisible}
              onEnded={(v, capture) => void onVideoEnded(v, capture)}
              onFailure={(reason) => interruptRef.current(reason)}
              registerHandle={(h) => {
                stimulusHandleRef.current = h;
              }}
            />
          ) : settings && preloaded ? (
            <StimulusImage
              key={ctx.nextIndex}
              url={preloaded.url}
              durationMs={settings.imageDurationMs}
              onVisible={onStimulusVisible}
              onElapsed={onStimulusElapsed}
              registerHandle={(h) => {
                stimulusHandleRef.current = h;
              }}
            />
          ) : (
            <div className="fixed inset-0" style={{ background: "#808080", cursor: "none" }} />
          );
        break;
      case "TRIAL_SAVE":
        screen = <div className="fixed inset-0" style={{ background: "#808080", cursor: "none" }} />;
        break;
      case "PAUSE":
        screen = <PauseScreen onResume={() => dispatch({ type: "RESUME_FROM_PAUSE" })} />;
        break;
      case "INTERRUPTED":
        screen = (
          <InterruptedScreen
            reason={ctx.interruption}
            kind={protocol?.stimulusKind ?? study.stimulusKind}
            remaining={Math.max(0, ctx.totalImages - ctx.nextIndex)}
            onResume={() => {
              queueRef.current?.retryFailed();
              dispatch({ type: "RESUME" });
            }}
            onFinish={() => dispatch({ type: "FINISH" })}
            onCancel={() => abort("participant_cancel")}
          />
        );
        break;
      case "COMPLETE":
        screen = (
          <CompleteScreen
            finalizing={finalizing}
            saveProblem={saveProblem}
            info={deletionInfo}
            onRetrySave={() => {
              setSaveProblem(0);
              void finalize(false);
            }}
            onFinishAnyway={() => {
              setSaveProblem(0);
              void finalize(true);
            }}
          />
        );
        break;
      case "ABORTED":
        screen = <AbortedScreen message={abortMessage} info={deletionInfo} />;
        break;
    }
  }

  const presenting = PRESENTATION_STATES.has(ctx.state);
  return (
    <div
      className="h-full"
      data-testid="participant-root"
      data-state={ctx.state}
      data-source={simulation ? "synthetic" : "webgazer"}
      style={presenting ? { cursor: "none", background: "#808080" } : undefined}
    >
      {screen}
      {/* Contenedor del rastreador (posicionamiento/calibración/validación). Nunca visible durante estímulos. */}
      <div
        ref={hostRef}
        data-testid="tracker-host"
        className="fixed inset-0 z-50 bg-white"
        style={{ display: hostVisible && !presenting ? "block" : "none" }}
      />
      {simulation && !presenting && <SimulationBanner />}
    </div>
  );
}
